import { Field, FieldArray, Formik } from 'formik'
import type { FieldProps, FormikProps } from 'formik'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import {
  DEFAULT_LOCALE,
  buildValidationRulesPayload,
  getContactTypeOptions,
  getNameTypeOptions,
  validationRulesYupSchema,
  TYPES_WITH_VALIDATION_RULES
} from '../questionFormOptions'
import { ValidationRulesSection } from '../validationRulesSection/ValidationRulesSection'
import { TYPES_WITH_CONDITIONALS } from '../../formsList/createFormModal/constants'
import type { QuestionOption } from '../addQuestionModal/AddQuestionModal.types'

import { useMutation, useQuery } from '@percent/utility'
import {
  AcknowledgeModal,
  ActionModal,
  Button,
  Flex,
  FormField,
  MarkdownTextArea,
  Modal,
  Select,
  TextInput
} from '@percent/lemonade'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { FormQuestionType } from '@percent/admin-dashboard/api/actions/grantProgram/grantProgram.types'
import type { EditQuestionFormValues, EditQuestionModalProps } from './EditQuestionModal.types'

const generateUid = () => crypto.randomUUID()

const TYPES_WITH_OPTIONS: FormQuestionType[] = ['select', 'award_recommendation', 'decision']

// `filter` drops the blank placeholder row the form always keeps at the end; every option loaded
// from the API has a key, so it only ever removes rows the user never filled in.
const buildOptionsPayload = (options: QuestionOption[]) =>
  options
    .filter(o => o.key || o.label)
    .map(o => ({
      // Identifies an existing row so the API updates it rather than deleting and recreating it.
      // Absent for options added in the form.
      ...(o.id ? { id: o.id } : {}),
      key: o.key,
      label: { [DEFAULT_LOCALE]: o.label },
      ...(o.conditionalQuestionIds.trim()
        ? {
            conditionalQuestionIds: o.conditionalQuestionIds
              .split(/[,\s]+/)
              .map(id => id.trim())
              .filter(Boolean)
          }
        : {})
    }))

const buildOptionsUpdate = (
  questionType: FormQuestionType,
  values: EditQuestionFormValues,
  detailMultiple: boolean | undefined,
  initialOptions: QuestionOption[]
) => {
  const shouldSendMultiple =
    questionType === 'select' &&
    (detailMultiple !== undefined ? values.multiple !== detailMultiple : values.multiple === true)

  // Sending `options` rewrites the question's option rows, so only send it when the options
  // themselves actually changed. Order-sensitive by design: reordering options is a change the
  // API needs to see.
  const optionsPayload = buildOptionsPayload(values.options)
  const optionsChanged = JSON.stringify(optionsPayload) !== JSON.stringify(buildOptionsPayload(initialOptions))

  return {
    ...(optionsChanged ? { options: optionsPayload } : {}),
    ...(shouldSendMultiple ? { multiple: values.multiple } : {})
  }
}

const buildTypeSpecificPayload = (
  questionType: FormQuestionType | undefined,
  values: EditQuestionFormValues,
  detailMultiple: boolean | undefined,
  initialOptions: QuestionOption[]
) => {
  if (questionType == null) return {}

  if (TYPES_WITH_OPTIONS.includes(questionType)) {
    return buildOptionsUpdate(questionType, values, detailMultiple, initialOptions)
  }

  switch (questionType) {
    case 'score':
      return { maxScore: Number(values.maxScore), scoreWeight: Number(values.scoreWeight) }
    case 'textarea':
      return values.maxLength !== '' ? { maxLength: Number(values.maxLength) } : {}
    case 'email':
      return values.contactType ? { contactType: values.contactType } : {}
    case 'name':
      return { nameType: values.nameType, contactType: values.contactType }
    case 'organisation_search':
      return { addOrganisationQuestions: values.addOrganisationQuestions.map(q => q.value).filter(Boolean) }
    default:
      return {}
  }
}

export function EditQuestionModal({ question, onClose, refresh }: Readonly<EditQuestionModalProps>) {
  const { t } = useTranslation()
  const { grantProgramService } = useServices()
  const contactTypeOptions = useMemo(() => getContactTypeOptions(t), [t])
  const nameTypeOptions = useMemo(() => getNameTypeOptions(t), [t])
  const [modalState, setModalState] = useState<'success' | 'error' | null>(null)

  const {
    data: questionDetail,
    isLoading: isLoadingDetail,
    refetch: refetchQuestionDetail
  } = useQuery(
    ['/v1/admin/grants/questions', question?.id ?? ''],
    grantProgramService.getStandaloneFormQuestion,
    { questionId: question?.id ?? '', locale: DEFAULT_LOCALE },
    { enabled: Boolean(question?.id) }
  )

  const [{ isPending }, { apiFunc: updateQuestion }] = useMutation(
    grantProgramService.updateStandaloneFormQuestion,
    () => {
      refresh()
      refetchQuestionDetail()
      setModalState('success')
    },
    () => setModalState('error')
  )

  const handleClose = useCallback(() => {
    onClose()
    setModalState(null)
  }, [onClose])

  const questionType = questionDetail?.type ?? question?.type

  const initialValues = useMemo(
    (): EditQuestionFormValues => ({
      key: questionDetail?.key ?? question?.key ?? '',
      label: questionDetail?.label ?? '',
      displayName: questionDetail?.dashboardLabel ?? '',
      placeholder: questionDetail?.placeholder ?? '',
      helpText: questionDetail?.description ?? '',
      instructions: questionDetail?.instructions ?? '',
      required: questionType === 'score' ? true : questionDetail?.required ?? question?.required ?? false,
      hidden: questionDetail?.hidden ?? false,
      autoTranslationsEnabled: questionDetail?.autoTranslationsEnabled ?? false,
      multiple: questionDetail?.multiple ?? false,
      options:
        questionDetail?.options?.map(o => ({
          uid: generateUid(),
          // `value` is the option row id until the API exposes `id` explicitly.
          id: o.id ?? o.value,
          key: o.key ?? o.value,
          label: o.label,
          conditionalQuestionIds: (o.conditionalQuestionIds ?? []).join(', ')
        })) ??
        (questionType === 'decision'
          ? [
              { uid: 'decision-pass', key: 'pass', label: '', conditionalQuestionIds: '' },
              { uid: 'decision-fail', key: 'fail', label: '', conditionalQuestionIds: '' }
            ]
          : [{ uid: generateUid(), key: '', label: '', conditionalQuestionIds: '' }]),
      maxScore: questionDetail?.maxScore ?? '',
      scoreWeight: questionDetail?.scoreWeight ?? (questionType === 'score' ? 1 : ''),
      maxLength: questionDetail?.maxLength ?? '',
      contactType: questionDetail?.contactType ?? '',
      nameType: questionDetail?.nameType ?? '',
      addOrganisationQuestions: questionDetail?.addOrganisationQuestions?.map(v => ({
        uid: generateUid(),
        value: v
      })) ?? [{ uid: generateUid(), value: '' }],
      validationRules:
        questionDetail?.validationRules?.map(r => ({
          uid: generateUid(),
          type: r.type,
          candidateField: r.type === 'candidate_field_comparison' ? r.candidateField : '',
          value: r.type === 'value_comparison' ? r.value : '',
          operator: r.operator,
          percentage: r.type === 'candidate_field_comparison' ? r.percentage : undefined,
          errorMessage: r.errorMessage ?? ''
        })) ?? []
    }),
    [questionDetail, question, questionType]
  )

  const existingOptionUids = useMemo(
    () => new Set(initialValues.options.filter(o => o.key).map(o => o.uid)),
    [initialValues.options]
  )

  const validationSchema = useMemo(() => {
    const hasOptionFields = questionType != null && TYPES_WITH_OPTIONS.includes(questionType)
    const isScore = questionType === 'score'
    const isName = questionType === 'name'

    const shape: Record<string, Yup.AnySchema> = {
      key: Yup.string().min(1).required(t('errorMessage.required')),
      label: Yup.string().min(1).required(t('errorMessage.required'))
    }

    if (hasOptionFields) {
      shape.options = Yup.array()
        .test('options-valid', t('errorMessage.required'), options => {
          const nonBlank = (options ?? []).filter(o => o.key || o.label)

          if (nonBlank.length === 0) return false

          return nonBlank.every(o => (o.key?.length ?? 0) > 0 && (o.label?.length ?? 0) > 0)
        })
        .required()
    }

    if (isScore) {
      shape.maxScore = Yup.number().integer().min(1).max(10).required(t('errorMessage.required'))
      shape.scoreWeight = Yup.number().positive().required(t('errorMessage.required'))
    }

    if (isName) {
      shape.nameType = Yup.string().required(t('errorMessage.required'))
      shape.contactType = Yup.string().required(t('errorMessage.required'))
    }

    shape.validationRules = validationRulesYupSchema(t)

    return Yup.object().shape(shape)
  }, [t, questionType])

  const handleSubmit = useCallback(
    (values: EditQuestionFormValues) => {
      if (!question) return

      const typeSpecificPayload = buildTypeSpecificPayload(
        questionType,
        values,
        questionDetail?.multiple,
        initialValues.options
      )

      const detailRequired = questionDetail?.required
      const shouldSendRequired =
        detailRequired !== undefined ? values.required !== detailRequired : values.required === true

      const detailAutoTranslations = questionDetail?.autoTranslationsEnabled
      const shouldSendAutoTranslations =
        detailAutoTranslations !== undefined
          ? values.autoTranslationsEnabled !== detailAutoTranslations
          : values.autoTranslationsEnabled === true

      const validationRulesPayload = questionType
        ? buildValidationRulesPayload(values.validationRules, questionType)
        : {}

      updateQuestion({
        questionId: question.id,
        payload: {
          type: questionType!,
          label: { [DEFAULT_LOCALE]: values.label },
          ...(values.displayName ? { dashboardLabel: values.displayName } : { dashboardLabel: null }),
          ...(values.placeholder ? { placeholder: { [DEFAULT_LOCALE]: values.placeholder } } : {}),
          ...(values.helpText ? { description: { [DEFAULT_LOCALE]: values.helpText } } : {}),
          ...(values.instructions !== (questionDetail?.instructions ?? '')
            ? { instructions: { [DEFAULT_LOCALE]: values.instructions } }
            : {}),
          ...(shouldSendRequired ? { required: values.required } : {}),
          ...(shouldSendAutoTranslations ? { autoTranslationsEnabled: values.autoTranslationsEnabled } : {}),
          hidden: values.hidden,
          ...typeSpecificPayload,
          ...validationRulesPayload
        }
      })
    },
    [question, questionType, updateQuestion, questionDetail, initialValues]
  )

  const renderContent = () => {
    if (modalState === 'success') {
      return (
        <AcknowledgeModal
          result="positive"
          title={t('grants.forms.questions.editModal.success.title')}
          description={t('grants.forms.questions.editModal.success.description')}
          buttonText={t('button.close')}
          handleClose={handleClose}
        />
      )
    }

    if (modalState === 'error') {
      return (
        <AcknowledgeModal
          result="negative"
          title={t('grants.forms.questions.editModal.error.title')}
          description={t('grants.forms.questions.editModal.error.description')}
          buttonText={t('button.close')}
          handleClose={handleClose}
        />
      )
    }

    const isDecision = questionType === 'decision'
    const hasOptions = questionType != null && TYPES_WITH_OPTIONS.includes(questionType)

    return (
      <Formik<EditQuestionFormValues>
        key={questionDetail?.id ?? 'loading'}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formik: FormikProps<EditQuestionFormValues>) => {
          const isDirty =
            formik.dirty ||
            formik.values.options.length !== initialValues.options.length ||
            formik.values.addOrganisationQuestions.length !== initialValues.addOrganisationQuestions.length ||
            formik.values.validationRules.length !== initialValues.validationRules.length

          return (
            <ActionModal
              title={t('grants.forms.questions.editModal.title')}
              primaryButtonText={t('grants.forms.questions.editModal.submit')}
              secondaryButtonText={t('button.cancel')}
              handleClose={handleClose}
              handleSubmit={formik.submitForm}
              loading={isPending || isLoadingDetail}
              disabled={!formik.isValid || !isDirty || isPending || isLoadingDetail}
              primaryBtnTestId="edit-question-submit-button"
              secondaryBtnTestId="edit-question-cancel-button"
              maxHeight="60vh"
            >
              <Flex direction="column" gap="lg">
                <Field name="label">
                  {({ field, meta }: FieldProps<string>) => (
                    <FormField
                      label={t('grants.forms.questions.addModal.label.label')}
                      necessity="required"
                      status={meta.touched && meta.error ? 'danger' : 'default'}
                      statusMessage={meta.touched && meta.error ? meta.error : undefined}
                    >
                      <TextInput
                        placeholder={t('grants.forms.questions.addModal.label.placeholder')}
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        status={meta.touched && meta.error ? 'danger' : 'default'}
                      />
                    </FormField>
                  )}
                </Field>

                <Field name="key">
                  {({ field }: FieldProps<string>) => (
                    <FormField label={t('grants.forms.questions.addModal.key.label')} necessity="required">
                      <TextInput
                        placeholder={t('grants.forms.questions.addModal.key.placeholder')}
                        name={field.name}
                        value={field.value}
                        disabled
                      />
                    </FormField>
                  )}
                </Field>

                <Field name="displayName">
                  {({ field }: FieldProps<string>) => (
                    <FormField label={t('grants.forms.questions.addModal.displayName.label')} necessity="optional">
                      <TextInput
                        placeholder={t('grants.forms.questions.addModal.displayName.placeholder')}
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                      />
                    </FormField>
                  )}
                </Field>

                <Field name="placeholder">
                  {({ field }: FieldProps<string>) => (
                    <FormField label={t('grants.forms.questions.addModal.placeholder.label')} necessity="optional">
                      <TextInput
                        placeholder={t('grants.forms.questions.addModal.placeholder.placeholder')}
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                      />
                    </FormField>
                  )}
                </Field>

                <Field name="instructions">
                  {({ field, form }: FieldProps<string>) => (
                    <FormField label={t('grants.forms.questions.addModal.instructions.label')} necessity="optional">
                      <MarkdownTextArea
                        name={field.name}
                        content={field.value}
                        placeholder={t('grants.forms.questions.addModal.instructions.placeholder')}
                        onChange={value => form.setFieldValue('instructions', value)}
                        testId="edit-question-instructions"
                      />
                    </FormField>
                  )}
                </Field>

                <Field name="helpText">
                  {({ field }: FieldProps<string>) => (
                    <FormField label={t('grants.forms.questions.addModal.helpText.label')} necessity="optional">
                      <TextInput
                        placeholder={t('grants.forms.questions.addModal.helpText.placeholder')}
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                      />
                    </FormField>
                  )}
                </Field>

                {questionType === 'textarea' && (
                  <Field name="maxLength">
                    {({ field, meta, form }: FieldProps) => (
                      <FormField
                        label={t('grants.forms.questions.addModal.maxLength.label')}
                        necessity="optional"
                        status={meta.touched && meta.error ? 'danger' : 'default'}
                        statusMessage={meta.touched && meta.error ? meta.error : undefined}
                      >
                        <TextInput
                          type="number"
                          min={1}
                          placeholder={t('grants.forms.questions.addModal.maxLength.placeholder')}
                          name={field.name}
                          value={field.value}
                          onChange={e => {
                            const val = e.target.valueAsNumber
                            form.setFieldValue('maxLength', Number.isNaN(val) ? '' : Math.floor(val))
                          }}
                          onBlur={field.onBlur}
                        />
                      </FormField>
                    )}
                  </Field>
                )}

                {questionType === 'score' && (
                  <>
                    <Field name="maxScore">
                      {({ field, meta, form }: FieldProps) => (
                        <FormField
                          label={t('grants.forms.questions.addModal.maxScore.label')}
                          necessity="required"
                          status={meta.touched && meta.error ? 'danger' : 'default'}
                          statusMessage={meta.touched && meta.error ? meta.error : undefined}
                        >
                          <TextInput
                            type="number"
                            min={1}
                            max={10}
                            step={1}
                            placeholder={t('grants.forms.questions.addModal.maxScore.placeholder')}
                            name={field.name}
                            value={field.value}
                            onChange={e => {
                              const val = e.target.valueAsNumber
                              form.setFieldValue('maxScore', Number.isNaN(val) ? '' : Math.floor(val))
                            }}
                            onBlur={field.onBlur}
                            status={meta.touched && meta.error ? 'danger' : 'default'}
                          />
                        </FormField>
                      )}
                    </Field>

                    <Field name="scoreWeight">
                      {({ field, meta, form }: FieldProps) => (
                        <FormField
                          label={t('grants.forms.questions.addModal.scoreWeight.label')}
                          necessity="required"
                          status={meta.touched && meta.error ? 'danger' : 'default'}
                          statusMessage={meta.touched && meta.error ? meta.error : undefined}
                        >
                          <TextInput
                            data-testid="edit-question-score-weight"
                            type="number"
                            min={0}
                            step="any"
                            placeholder={t('grants.forms.questions.addModal.scoreWeight.placeholder')}
                            name={field.name}
                            value={field.value}
                            onChange={e => {
                              const val = e.target.valueAsNumber
                              form.setFieldValue('scoreWeight', Number.isNaN(val) ? '' : val)
                            }}
                            onBlur={field.onBlur}
                            status={meta.touched && meta.error ? 'danger' : 'default'}
                          />
                        </FormField>
                      )}
                    </Field>
                  </>
                )}

                {questionType === 'email' && (
                  <Field name="contactType">
                    {({ field, form }: FieldProps) => (
                      <FormField label={t('grants.forms.questions.addModal.contactType.label')} necessity="optional">
                        <Select
                          name={field.name}
                          options={contactTypeOptions}
                          defaultValue={contactTypeOptions.find(o => o.value === field.value)}
                          onChange={e => form.setFieldValue('contactType', e.value)}
                          placeholder={t('grants.forms.questions.addModal.contactType.placeholder')}
                        />
                      </FormField>
                    )}
                  </Field>
                )}

                {questionType === 'name' && (
                  <>
                    <Field name="nameType">
                      {({ field, meta, form }: FieldProps) => (
                        <FormField
                          label={t('grants.forms.questions.addModal.nameType.label')}
                          necessity="required"
                          status={meta.touched && meta.error ? 'danger' : 'default'}
                          statusMessage={meta.touched && meta.error ? meta.error : undefined}
                        >
                          <Select
                            name={field.name}
                            options={nameTypeOptions}
                            defaultValue={nameTypeOptions.find(o => o.value === field.value)}
                            onChange={e => form.setFieldValue('nameType', e.value)}
                            placeholder={t('grants.forms.questions.addModal.nameType.placeholder')}
                          />
                        </FormField>
                      )}
                    </Field>
                    <Field name="contactType">
                      {({ field, meta, form }: FieldProps) => (
                        <FormField
                          label={t('grants.forms.questions.addModal.contactType.label')}
                          necessity="required"
                          status={meta.touched && meta.error ? 'danger' : 'default'}
                          statusMessage={meta.touched && meta.error ? meta.error : undefined}
                        >
                          <Select
                            name={field.name}
                            options={contactTypeOptions}
                            defaultValue={contactTypeOptions.find(o => o.value === field.value)}
                            onChange={e => form.setFieldValue('contactType', e.value)}
                            placeholder={t('grants.forms.questions.addModal.contactType.placeholder')}
                          />
                        </FormField>
                      )}
                    </Field>
                  </>
                )}

                {questionType === 'organisation_search' && (
                  <FieldArray name="addOrganisationQuestions">
                    {({ push, remove }) => (
                      <FormField
                        label={t('grants.forms.questions.addModal.addOrganisationQuestions.label')}
                        necessity="optional"
                      >
                        <Flex direction="column" gap="sm">
                          {formik.values.addOrganisationQuestions.map((orgQ, index) => (
                            <Flex key={orgQ.uid} gap="sm" align="center">
                              <div style={{ flex: 1 }}>
                                <Field name={`addOrganisationQuestions.${index}.value`}>
                                  {({ field }: FieldProps<string>) => (
                                    <TextInput
                                      placeholder={t(
                                        'grants.forms.questions.addModal.addOrganisationQuestions.placeholder'
                                      )}
                                      name={field.name}
                                      value={field.value}
                                      onChange={field.onChange}
                                      onBlur={field.onBlur}
                                    />
                                  )}
                                </Field>
                              </div>
                              {formik.values.addOrganisationQuestions.length > 1 && (
                                <Button
                                  variant="reject"
                                  size="small"
                                  onClick={() => remove(index)}
                                  data-testid={`edit-remove-org-question-${index}`}
                                >
                                  {t('button.remove')}
                                </Button>
                              )}
                            </Flex>
                          ))}
                          <Button
                            variant="secondary"
                            size="small"
                            onClick={() => push({ uid: generateUid(), value: '' })}
                            data-testid="edit-add-org-question-button"
                          >
                            {t('grants.forms.questions.addModal.addOrganisationQuestions.addButton')}
                          </Button>
                        </Flex>
                      </FormField>
                    )}
                  </FieldArray>
                )}

                {hasOptions && (
                  <FieldArray name="options">
                    {({ push, remove }) => (
                      <FormField label={t('grants.forms.questions.addModal.options.label')} necessity="required">
                        <Flex direction="column" gap="sm">
                          {formik.values.options.map((option, index) => (
                            <Flex key={option.uid} gap="sm" align="flex-start">
                              {!isDecision && (
                                <div style={{ flex: 1 }}>
                                  <Field name={`options.${index}.key`}>
                                    {({ field, meta }: FieldProps<string>) => {
                                      const isExisting = existingOptionUids.has(option.uid)

                                      return (
                                        <FormField
                                          label={
                                            index === 0 ? t('grants.forms.questions.addModal.options.key') : undefined
                                          }
                                          status={!isExisting && meta.touched && meta.error ? 'danger' : 'default'}
                                          statusMessage={
                                            !isExisting && meta.touched && meta.error ? meta.error : undefined
                                          }
                                        >
                                          <TextInput
                                            placeholder={t('grants.forms.questions.addModal.options.keyPlaceholder')}
                                            name={field.name}
                                            value={field.value}
                                            onChange={isExisting ? undefined : field.onChange}
                                            onBlur={isExisting ? undefined : field.onBlur}
                                            disabled={isExisting}
                                            status={!isExisting && meta.touched && meta.error ? 'danger' : 'default'}
                                            readOnly={isExisting}
                                          />
                                        </FormField>
                                      )
                                    }}
                                  </Field>
                                </div>
                              )}

                              <div style={{ flex: 1 }}>
                                <Field name={`options.${index}.label`}>
                                  {({ field, meta }: FieldProps<string>) => (
                                    <FormField
                                      label={
                                        isDecision
                                          ? `${option.key} — ${t(
                                              'grants.forms.questions.addModal.options.label_field'
                                            )}`
                                          : index === 0
                                          ? t('grants.forms.questions.addModal.options.label_field')
                                          : undefined
                                      }
                                      status={meta.touched && meta.error ? 'danger' : 'default'}
                                      statusMessage={meta.touched && meta.error ? meta.error : undefined}
                                    >
                                      <TextInput
                                        placeholder={t('grants.forms.questions.addModal.options.labelPlaceholder')}
                                        name={field.name}
                                        value={field.value}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        status={meta.touched && meta.error ? 'danger' : 'default'}
                                      />
                                    </FormField>
                                  )}
                                </Field>
                              </div>

                              {questionType != null && TYPES_WITH_CONDITIONALS.includes(questionType) && (
                                <div style={{ flex: 2 }}>
                                  <Field name={`options.${index}.conditionalQuestionIds`}>
                                    {({ field }: FieldProps<string>) => (
                                      <FormField
                                        label={
                                          index === 0
                                            ? t('grants.forms.questions.addModal.options.conditionalQuestionIds')
                                            : undefined
                                        }
                                      >
                                        <TextInput
                                          data-testid={`edit-option-conditional-ids-${index}`}
                                          placeholder={t(
                                            'grants.forms.questions.addModal.options.conditionalQuestionIdsPlaceholder'
                                          )}
                                          name={field.name}
                                          value={field.value}
                                          onChange={field.onChange}
                                          onBlur={field.onBlur}
                                        />
                                      </FormField>
                                    )}
                                  </Field>
                                </div>
                              )}

                              {!isDecision && formik.values.options.length > 1 && (
                                <div style={index === 0 ? { marginTop: 32 } : undefined}>
                                  <Button
                                    variant="reject"
                                    size="small"
                                    onClick={() => remove(index)}
                                    data-testid={`edit-remove-option-${index}`}
                                  >
                                    {t('button.remove')}
                                  </Button>
                                </div>
                              )}
                            </Flex>
                          ))}

                          {!isDecision && (
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() =>
                                push({ uid: generateUid(), key: '', label: '', conditionalQuestionIds: '' })
                              }
                              data-testid="edit-add-option-button"
                            >
                              {t('grants.forms.questions.addModal.options.addButton')}
                            </Button>
                          )}
                        </Flex>
                      </FormField>
                    )}
                  </FieldArray>
                )}

                {questionType === 'select' && (
                  <Field name="multiple">
                    {({ field, form }: FieldProps<boolean>) => (
                      <FormField
                        label={t('grants.forms.questions.addModal.multiple.label')}
                        necessity="optional"
                        data-testid="edit-question-multiple-field"
                      >
                        <Select
                          name="multiple"
                          options={[
                            { label: t('button.yes'), value: String(true) },
                            { label: t('button.no'), value: String(false) }
                          ]}
                          defaultValue={{
                            label: field.value ? t('button.yes') : t('button.no'),
                            value: String(field.value)
                          }}
                          onChange={option => form.setFieldValue('multiple', option.value === String(true))}
                          data-testid="edit-question-multiple-select"
                        />
                      </FormField>
                    )}
                  </Field>
                )}

                {questionType && TYPES_WITH_VALIDATION_RULES.includes(questionType) && (
                  <ValidationRulesSection validationRules={formik.values.validationRules} testIdPrefix="edit" />
                )}

                <Field name="required">
                  {({ field, form }: FieldProps<boolean>) => {
                    // Score questions are always required, so the toggle is forced on and disabled.
                    const isScore = questionType === 'score'
                    const value = isScore || field.value

                    return (
                      <FormField
                        label={t('grants.forms.questions.addModal.required.label')}
                        necessity="required"
                        data-testid="edit-question-required-field"
                      >
                        <Select
                          key={isScore ? 'required-locked' : 'required-editable'}
                          name="required"
                          disabled={isScore}
                          options={[
                            { label: t('button.yes'), value: String(true) },
                            { label: t('button.no'), value: String(false) }
                          ]}
                          defaultValue={{
                            label: value ? t('button.yes') : t('button.no'),
                            value: String(value)
                          }}
                          onChange={option => form.setFieldValue('required', option.value === String(true))}
                          data-testid="edit-question-required-select"
                        />
                      </FormField>
                    )
                  }}
                </Field>

                <Field name="hidden">
                  {({ field, form }: FieldProps<boolean>) => (
                    <FormField
                      label={t('grants.forms.questions.addModal.hidden.label')}
                      necessity="optional"
                      data-testid="edit-question-hidden-field"
                    >
                      <Select
                        name="hidden"
                        options={[
                          { label: t('button.yes'), value: String(true) },
                          { label: t('button.no'), value: String(false) }
                        ]}
                        defaultValue={{
                          label: field.value ? t('button.yes') : t('button.no'),
                          value: String(field.value)
                        }}
                        onChange={option => form.setFieldValue('hidden', option.value === String(true))}
                        data-testid="edit-question-hidden-select"
                      />
                    </FormField>
                  )}
                </Field>

                <Field name="autoTranslationsEnabled">
                  {({ field, form }: FieldProps<boolean>) => (
                    <FormField
                      label={t('grants.forms.questions.autoTranslationsEnabled.label')}
                      necessity="optional"
                      data-testid="edit-question-auto-translations-enabled-field"
                    >
                      <Select
                        name="autoTranslationsEnabled"
                        options={[
                          { label: t('button.yes'), value: String(true) },
                          { label: t('button.no'), value: String(false) }
                        ]}
                        defaultValue={{
                          label: field.value ? t('button.yes') : t('button.no'),
                          value: String(field.value)
                        }}
                        onChange={option =>
                          form.setFieldValue('autoTranslationsEnabled', option.value === String(true))
                        }
                        data-testid="edit-question-auto-translations-enabled-select"
                      />
                    </FormField>
                  )}
                </Field>
              </Flex>
            </ActionModal>
          )
        }}
      </Formik>
    )
  }

  return (
    <Modal open={!!question} onClose={handleClose} aria-labelledby="edit-question-modal" showModalOverflow={false}>
      {renderContent()}
    </Modal>
  )
}
